import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useSessionTimer from "../hooks/useSessionTimer";
import { logout } from "../utils/userSessionStorage";
import { rejectSession } from "../utils/rejectSession";
import { handleRedirectBack } from "../../../shared/utils/navigationUtils";
import { logger } from "../../../shared/utils/logger.client";
import { useTranslation } from "react-i18next";

interface SessionExpiredNoticeProps {
  /** Show modal when remaining time <= this; defaults to 10s */
  warnBeforeMs?: number;
}

const SessionExpiredNotice: React.FC<SessionExpiredNoticeProps> = ({ warnBeforeMs }) => {
  const navigate = useNavigate();
  const { getRemainingTime, startTimer, resetTimerExpired } = useSessionTimer();
  const { t } = useTranslation();

  const thresholdMs = warnBeforeMs ?? 10_000;

  const [remainingMs, setRemainingMs] = useState<number>(getRemainingTime());
  const [show, setShow] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const didRunExpiryFlowRef = useRef(false);

  const clearTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  useEffect(() => {
    const now = getRemainingTime();
    setRemainingMs(now);
    logger.debug(
        `[SessionExpiredNotice] mounted. remainingMs=${now} thresholdMs=${thresholdMs}`
    );

    clearTick();
    tickRef.current = setInterval(() => {
      const r = getRemainingTime();
      setRemainingMs(r);
    }, 1000);

    return clearTick;
  }, [getRemainingTime, clearTick, thresholdMs]);

  useEffect(() => {
    const onDisable = () => {
      logger.debug("[SessionExpiredNotice] Timer disabled externally → stopping modal + countdown");
      clearTick();
      setShow(false);
      didRunExpiryFlowRef.current = true;
    };

    window.addEventListener("sessionTimerDisabled", onDisable);
    return () => window.removeEventListener("sessionTimerDisabled", onDisable);
  }, [clearTick]);

  useEffect(() => {
    if (remainingMs <= 0) {
      if (didRunExpiryFlowRef.current) return;
      didRunExpiryFlowRef.current = true;

      logger.debug("[SessionExpiredNotice] remainingMs<=0 → running expiry flow");

      setIsLoggingOut(true);
      setShow(false);
      clearTick();

      (async () => {
        try {
          logger.debug("[SessionExpiredNotice] Step 1/2: rejectSession()");
          const rejected = await rejectSession(navigate);
          logger.debug(
              `[SessionExpiredNotice] rejectSession() → ${rejected ? "success" : "failed"}`
          );
        } catch (e) {
          logger.warn("[SessionExpiredNotice] rejectSession() threw:", e);
        }

        try {
          logger.debug("[SessionExpiredNotice] Step 2/2: handleRedirectBack()");
          await handleRedirectBack(navigate, true);
          logger.debug("[SessionExpiredNotice] handleRedirectBack() → done");
        } catch (e) {
          logger.warn("[SessionExpiredNotice] handleRedirectBack() threw:", e);
          try {
            logger.debug("[SessionExpiredNotice] Fallback → navigate('/login')");
            navigate("/login", { replace: true });
          } catch {}
        }
      })();

      return;
    }

    const showModal = remainingMs <= thresholdMs;
    if (showModal !== show) {
      logger.debug(
          `[SessionExpiredNotice] modal ${showModal ? "OPEN" : "CLOSE"} (remainingMs=${remainingMs})`
      );
    }
    setShow(showModal);
  }, [remainingMs, thresholdMs, navigate, clearTick, show]);

  const handleContinue = () => {
    logger.debug("[SessionExpiredNotice] Continue clicked → restarting session timer");
    clearTick();
    resetTimerExpired?.();
    startTimer();
    setShow(false);
    didRunExpiryFlowRef.current = false;

    const r = getRemainingTime();
    setRemainingMs(r);
    tickRef.current = setInterval(() => {
      const left = getRemainingTime();
      setRemainingMs(left);
    }, 1000);
  };

  if (isLoggingOut) {
    return (
        <>
          <style>
            {`@keyframes spin {from {transform: rotate(0deg);} to {transform: rotate(360deg);} }`}
          </style>
          <div style={styles.overlay} />
          <div style={styles.modal} role="alert" aria-live="assertive" aria-busy="true">
            <div style={styles.spinner} aria-hidden />
            <p style={{ marginTop: 12 }}>{t("sessionExpired.endingTitle")}</p>
            <p style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              {t("sessionExpired.endingSubtitle")}
            </p>
          </div>
        </>
    );
  }

  if (!show) return null;

  const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
  return (
      <>
        <div style={styles.overlay} />
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="sess-exp-title">
          <p id="sess-exp-title">{t("sessionExpired.warningTitle")}</p>
          <p>
            {t("sessionExpired.warningPrefix")} <strong>{secondsLeft}</strong>{" "}
            {secondsLeft === 1 ? t("sessionExpired.second") : t("sessionExpired.seconds")}.
          </p>
          <button onClick={handleContinue} style={styles.button}>
            {t("sessionExpired.continue")}
          </button>
        </div>
      </>
  );
};

const styles = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 99999,
  },
  modal: {
    position: "fixed" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#fff",
    padding: "20px 30px",
    borderRadius: 8,
    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
    zIndex: 100000,
    textAlign: "center" as const,
    minWidth: 320,
    fontWeight: "bold" as const,
  },
  button: {
    marginTop: 16,
    padding: "10px 20px",
    fontSize: 16,
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "4px solid #eee",
    borderTop: "4px solid #1976d2",
    animation: "spin 0.9s linear infinite",
    margin: "0 auto",
  },
};

export default SessionExpiredNotice;
